import React, { useState } from 'react';
import { 
  EnvelopeIcon, 
  ShieldCheckIcon,
  UserGroupIcon,
  EyeIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/apiClient';
import Modal from './Modal';

interface InviteMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface InviteData {
  email: string;
  role: string;
}

const InviteMemberModal: React.FC<InviteMemberModalProps> = ({ isOpen, onClose }) => {
  const [inviteData, setInviteData] = useState<InviteData>({
    email: '',
    role: 'team_member'
  });
  const [errors, setErrors] = useState<{ email?: string; role?: string }>({});

  const queryClient = useQueryClient();

  // Invite member mutation
  const inviteMutation = useMutation({
    mutationFn: async (data: InviteData) => {
      return apiClient.customRequest('/api/team/invites', {
        method: 'POST',
        body: data
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teamMembers'] });
      onClose();
      setInviteData({ email: '', role: 'team_member' });
      setErrors({});
    },
    onError: (error: Error) => {
      console.error('Failed to invite member:', error);
      // You could show a toast notification here
    }
  });

  const validateForm = () => {
    const newErrors: { email?: string; role?: string } = {};

    if (!inviteData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!inviteData.role) {
      newErrors.role = 'Role is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      inviteMutation.mutate(inviteData);
    }
  };

  const handleClose = () => {
    if (!inviteMutation.isPending) {
      onClose();
      setInviteData({ email: '', role: 'team_member' });
      setErrors({});
    }
  };

  const getRoleInfo = (role: string) => {
    switch (role) {
      case 'tenant_admin':
        return { 
          text: 'Admin', 
          icon: ShieldCheckIcon, 
          description: 'Full access to team management and all settings',
          color: 'text-blue-600'
        };
      case 'team_member':
        return { 
          text: 'Member', 
          icon: UserGroupIcon, 
          description: 'Standard access to core features and collaboration',
          color: 'text-green-600'
        };
      case 'viewer':
        return { 
          text: 'Viewer', 
          icon: EyeIcon, 
          description: 'Read-only access to view data and reports',
          color: 'text-gray-600'
        };
      default:
        return { 
          text: 'Member', 
          icon: UserGroupIcon, 
          description: 'Standard access to core features and collaboration',
          color: 'text-green-600'
        };
    }
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={handleClose} 
      title="Invite a new team member"
      maxWidth="lg"
    >
      <div className="space-y-6">
        <p className="text-sm text-muted-foreground">
          Send an invitation to join your team. They'll receive an email with instructions to get started.
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Email Input */}
          <div>
            <label htmlFor="email" className="block text-sm font-semibold text-foreground mb-3">
              Email Address
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <EnvelopeIcon className="h-5 w-5 text-muted-foreground" />
              </div>
              <input
                type="email"
                id="email"
                value={inviteData.email}
                onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                className={`block w-full pl-12 pr-4 py-3 border-2 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm transition-colors duration-200 bg-background text-foreground ${
                  errors.email ? 'border-destructive bg-destructive/5' : 'border-border hover:border-border/80 focus:bg-background'
                }`}
                placeholder="colleague@company.com"
                disabled={inviteMutation.isPending}
              />
            </div>
            {errors.email && (
              <p className="mt-2 text-sm text-destructive flex items-center">
                <span className="inline-block w-1 h-1 bg-destructive rounded-full mr-2"></span>
                {errors.email}
              </p>
            )}
          </div>

          {/* Role Selection */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-3">
              Team Role
            </label>
            <div className="space-y-3">
              {['team_member', 'tenant_admin', 'viewer'].map((role) => {
                const roleInfo = getRoleInfo(role);
                const RoleIcon = roleInfo.icon;
                const isSelected = inviteData.role === role;
                
                return (
                  <label
                    key={role}
                    className={`relative flex cursor-pointer rounded-xl border-2 p-4 transition-all duration-200 hover:shadow-sm ${
                      isSelected
                        ? 'border-primary bg-primary/5 shadow-sm'
                        : 'border-border hover:border-border/80 hover:bg-muted/50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="role"
                      value={role}
                      checked={isSelected}
                      onChange={(e) => setInviteData({ ...inviteData, role: e.target.value })}
                      className="sr-only"
                      disabled={inviteMutation.isPending}
                    />
                    <div className="flex items-start w-full">
                      <div className={`flex-shrink-0 p-2 rounded-lg ${isSelected ? 'bg-background' : 'bg-muted'}`}>
                        <RoleIcon className={`h-5 w-5 ${isSelected ? roleInfo.color : 'text-muted-foreground'}`} />
                      </div>
                      <div className="ml-4 flex-1">
                        <div className="flex items-center justify-between">
                          <span className={`text-sm font-semibold ${
                            isSelected ? 'text-primary' : 'text-foreground'
                          }`}>
                            {roleInfo.text}
                          </span>
                          {isSelected && (
                            <div className="flex-shrink-0">
                              <div className="h-5 w-5 rounded-full bg-primary flex items-center justify-center">
                                <CheckIcon className="h-3 w-3 text-primary-foreground" />
                              </div>
                            </div>
                          )}
                        </div>
                        <p className={`text-sm mt-1 ${
                          isSelected ? 'text-primary/80' : 'text-muted-foreground'
                        }`}>
                          {roleInfo.description}
                        </p>
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
            {errors.role && (
              <p className="mt-2 text-sm text-destructive flex items-center">
                <span className="inline-block w-1 h-1 bg-destructive rounded-full mr-2"></span>
                {errors.role}
              </p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-border">
            <button
              type="button"
              onClick={handleClose}
              disabled={inviteMutation.isPending}
              className="px-6 py-2.5 text-sm font-medium text-muted-foreground bg-background border border-border rounded-lg hover:bg-muted hover:border-border/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={inviteMutation.isPending}
              className="px-6 py-2.5 text-sm font-semibold text-primary-foreground bg-primary border border-transparent rounded-lg hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-colors duration-200"
            >
              {inviteMutation.isPending ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground mr-2"></div>
                  Sending Invite...
                </div>
              ) : (
                'Send Invite'
              )}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default InviteMemberModal;
